import { Invoice, InvoiceDetail } from "./Invoice";
import {
	getCustomer, getVendor, getSplit,
	getCategory, getLocation, getAccount,
} from "../stores/Common";
import { formatCurrency, formatDate } from "../services/shared";

export interface FieldChange<T> {
	original: T | undefined;
	current: T | undefined;
}

export interface InvoiceDetailChange {
	changes: DetailChangesType;
	isNew?: boolean;
	isDeleted?: boolean;
	lineItem: number;
}

export type ChangesType = Partial<Record<keyof Omit<Invoice, 'id' | 'invoiceDetails' | 'history' | 'validations' | 'paymentRequests'>, FieldChange<any>>>;
type DetailChangesType = Partial<Record<keyof Omit<InvoiceDetail, 'id' | 'invoiceId' | 'categoryMarkup'>, FieldChange<any>>>;

export interface InvoiceChange {
	id: string;
	url?: string;
	changes: ChangesType;
	detailChanges?: InvoiceDetailChange[];
	authorId?: string;
	approverId?: string;
}

const INVOICE_IGNORE_KEYS: (keyof Invoice)[] = [
	'id',
	'invoiceDetails',
	'history',
	'paymentRequests',
	'statusDate',
	'actor',
	'status',
	'statusReason',
	'attachment'
];

const DETAIL_IGNORE_KEYS: (keyof InvoiceDetail)[] = [
	'id',
	'invoiceId',
	'categoryMarkup',
];

const CHANGES_FIELD_MAPPER = {
	"vendorId": "Vendor",
	"vendorAccountNumber": "Account Number",
	"contractNumber": "Contract Number",
	"invoiceNumber": "Invoice Number",
	"invoiceTotal": "Invoice Total",
	"receivedDate": "Received Date",
	"invoiceDate": "Invoice Date",
	"dueDate": "Due Date",
	"paymentType": "Payment Type",
	"attachmentUrl": "Attachment",
	"notes": "Notes",
	"accountId": "Account",
	"serviceTillDate": "Service Till Date",
	"customerId": "Customer",
	"categoryId": "Category",
	"serviceFromDate": "Service From Date",
	"locationId": "Location",
	"splitId": "Split",
	"amount": "Amount",
	"detail": "Description",
	"lineItem": "Line Item",
}

export function getInvoiceDifferences(original: Invoice | undefined, current: Invoice): InvoiceChange | null {
	if (!original || !current) return null;

	const changes: Partial<Record<keyof Invoice, FieldChange<any>>> = {};

	for (const key of Object.keys(original) as (keyof Invoice)[]) {
		if (INVOICE_IGNORE_KEYS.includes(key)) continue;

		let originalVal = original[key];
		let currentVal = current[key];

		if ((key as string) === 'vendorId') {
			originalVal = getVendor(originalVal as string);
			currentVal = getVendor(currentVal as string);
		}

		if ((key as string) === 'dueDate') {
			originalVal = typeof originalVal === 'string' ? formatDate(originalVal) : originalVal;
			currentVal = typeof currentVal === 'string' ? formatDate(currentVal) : currentVal;
		}

		if ((key as string) === 'invoiceDate') {
			originalVal = typeof originalVal === 'string' ? formatDate(originalVal) : originalVal;
			currentVal = typeof currentVal === 'string' ? formatDate(currentVal) : currentVal;
		}

		if ((key as string) === 'invoiceTotal') {
			originalVal = typeof originalVal === 'number' || originalVal == null ? formatCurrency(originalVal) : originalVal;
			currentVal = typeof currentVal === 'number' || currentVal == null ? formatCurrency(currentVal) : currentVal;
		}

		if (JSON.stringify(originalVal) !== JSON.stringify(currentVal)) {
			changes[key as keyof Invoice] = { original: originalVal, current: currentVal };
		}
	}

	const detailChanges = diffInvoiceDetails(
		original.invoiceDetails,
		current.invoiceDetails
	);

	if (Object.keys(changes).length === 0 && detailChanges.length === 0) return null;

	return {
		id: original.id,
		changes: replaceFieldNames(changes),
		...(detailChanges.length > 0 && { detailChanges }),
	};
}

function replaceFieldNames<T extends PropertyKey>(changes: Partial<Record<T, FieldChange<any>>>) {
	var result = Object.fromEntries(
		Object.entries(changes).map(([key, value]) => [
			CHANGES_FIELD_MAPPER[key as keyof typeof CHANGES_FIELD_MAPPER] ?? key,
			value
		])
	);
	return result;
}

function diffInvoiceDetails(original: InvoiceDetail[] | undefined = [], current: InvoiceDetail[] = []): InvoiceDetailChange[] {
	if (!original) return [];

	const result: InvoiceDetailChange[] = [];

	const originalMap = new Map(original.map(d => [d.id, d]));
	const currentMap = new Map(current.map((d, i) => [d.id || `__new_${i}`, d]));

	// Deleted Details
	for (const [id, detail] of originalMap) {
		if (!currentMap.has(id)) {
			result.push({
				changes: {
					detail: { original: detail.detail, current: null },
					amount: { original: formatCurrency(detail.amount), current: null }
				},
				isDeleted: true,
				lineItem: detail.lineItem || 0
			});
		}
	}

	// Added Details
	for (const [id, detail] of currentMap) {
		if (!originalMap.has(id)) {
			const changes: DetailChangesType = {};

			for (const key of Object.keys(detail) as (keyof InvoiceDetail)[]) {
				if (DETAIL_IGNORE_KEYS.includes(key)) continue;

				const k = key as keyof Omit<InvoiceDetail, 'id' | 'invoiceId' | 'categoryMarkup'>;

				let currentVal = detail[key];
				if (currentVal === undefined) continue; // skip if both would be undefined

				if ((key as string) === 'accountId') {
					currentVal = getAccount(currentVal as string);
				}
				if ((key as string) === 'categoryId') {
					currentVal = getCategory(currentVal as string);
				}
				if ((key as string) === 'customerId') {
					currentVal = getCustomer(currentVal as string);
				}
				if ((key as string) === 'splitId') {
					currentVal = getSplit(currentVal as string);
				}
				if ((key as string) === 'locationId') {
					currentVal = getLocation(currentVal as string);
				}

				changes[k] = { original: undefined, current: currentVal };
			}

			result.push({ changes, isNew: true, lineItem: detail.lineItem || 0 });
		}
	}

	// Modified Details
	for (const [id, currentDetail] of currentMap) {
		const originalDetail = originalMap.get(id);
		if (!originalDetail) continue;

		const changes: DetailChangesType = {};

		for (const key of Object.keys(currentDetail) as (keyof InvoiceDetail)[]) {
			if (DETAIL_IGNORE_KEYS.includes(key)) continue;
			const k = key as keyof Omit<InvoiceDetail, 'id' | 'invoiceId' | 'categoryMarkup'>;

			let originalVal = originalDetail[key];
			let currentVal = currentDetail[key];

			if ((key as string) === 'accountId') {
				originalVal = getAccount(originalVal as string);
				currentVal = getAccount(currentVal as string);
			}

			if ((key as string) === 'categoryId') {
				originalVal = getCategory(originalVal as string);
				currentVal = getCategory(currentVal as string);
			}

			if ((key as string) === 'customerId') {
				originalVal = getCustomer(originalVal as string);
				currentVal = getCustomer(currentVal as string);
			}

			if ((key as string) === 'splitId') {
				originalVal = getSplit(originalVal as string);
				currentVal = getSplit(currentVal as string);
			}

			if ((key as string) === 'locationId') {
				originalVal = getLocation(originalVal as string);
				currentVal = getLocation(currentVal as string);
			}

			if ((key as string) === 'serviceFromDate') {
				originalVal = typeof originalVal === 'string' ? formatDate(originalVal) : originalVal;
				currentVal = typeof currentVal === 'string' ? formatDate(currentVal) : currentVal;
			}

			if ((key as string) === 'serviceTillDate') {
				originalVal = typeof originalVal === 'string' ? formatDate(originalVal) : originalVal;
				currentVal = typeof currentVal === 'string' ? formatDate(currentVal) : currentVal;
			}

			if ((key as string) === 'amount') {
				originalVal = typeof originalVal === 'number' || originalVal == null ? formatCurrency(originalVal) : originalVal;
				currentVal = typeof currentVal === 'number' || currentVal == null ? formatCurrency(currentVal) : currentVal;
			}

			if (originalVal === undefined && currentVal === undefined) continue; // both undefined, skip

			if (JSON.stringify(originalVal) !== JSON.stringify(currentVal)) {
				changes[k] = { original: originalVal, current: currentVal };
			}
		}

		if (Object.keys(changes).length > 0) {
			result.push({ changes, lineItem: currentDetail.lineItem || 0 });
		}
	}

	// Apply field name mapping to all changes at once
	return result.map(item => ({
		...item,
		changes: replaceFieldNames(item.changes)
	}));
}
